// ── The shared logic of the ring screens (#608) ─────────────────────────────
// The ring display (a TV at the ring) and the broadcast overlay (#604) render
// from one feed (`hooks/useRingFeed.ts`) and choose their view with one rule.
// Each page keeps its own look. The words come from the dictionary that the
// page gives: both pages give the English one (operator, 2026-10-15).
// ─────────────────────────────────────────────────────────────────────────────
import { useLayoutEffect, useState } from 'react';
import type { Translations } from '../../i18n';
import { divisionLabel, stageLabel } from '../../i18n/labels';
import { outcomeLabel } from '../../utils/boutOutcomes';
import { boutRounds, roundFormula } from '../../utils/tournament';
import type { RingFeed } from '../../hooks/useRingFeed';
import type { BoutCornerColour } from '../../utils/boutCorner';
import type { Bout, BoutVerdict, PublicRoundScore } from '../../types';

/** The design frame of a ring screen. The page scales it to the screen. */
export const FRAME_W = 1920;
export const FRAME_H = 1080;

/** The words of a ring screen. The helpers are the same functions that the
 *  other pages use. They take the dictionary that the page gives (#604 review). */
export function ringWords(T: Translations) {
  const stage = (s: string | null | undefined) => (s ? stageLabel(T, s) : '');
  const division = (g: string | null | undefined) => (g ? divisionLabel(T, g) : '');
  /** The name of an outcome (English in each language). A code with no name
   *  gives '', so the screen does not show the code two times. */
  const outcome = (code: string | null | undefined) => {
    const name = outcomeLabel(code);
    return name === code ? '' : name;
  };
  return { T, stage, division, outcome };
}
export type RingWords = ReturnType<typeof ringWords>;

/** The age group of a bout: the class line and the round formula read this one value. */
export const ageGroupOf = (b: Bout) => b.bout_age_group || b.red_age_group || b.blue_age_group || '';

/** The round formula of a bout ("3 × 3:00"); null for an unknown class. */
export const roundFormulaOf = (b: Bout) => roundFormula(ageGroupOf(b), b.rounds);

/** The class line of a bout: "Male · Elite · -71". The weight class shows as
 *  its label, with its minus and no unit, as on every page (operator, 2026-10-15). */
export function classLine(b: Bout, w: RingWords): string {
  const wc = b.bout_weight_class || b.red_weight_class || b.blue_weight_class || '';
  return [w.division(b.red_gender || b.blue_gender), ageGroupOf(b), wc].filter(Boolean).join(' · ');
}

export type Corner = BoutCornerColour;

/** The colours of the backup timer of the jury (#600, #601) on a white
 *  card: amber while stopped (operator, 2026-10-14), red at 0:00. The ring
 *  display and the overlay use the same colours (#608). */
export const TIMER_COLOR: Record<'ready' | 'running' | 'paused' | 'done', string> = {
  ready: 'text-slate-900', running: 'text-slate-900', paused: 'text-amber-600', done: 'text-red-600',
};

/** A corner panel: live, or the winner or the loser of a result. */
export type PanelState = 'live' | 'winner' | 'loser';

/** The bright look of a corner panel (#608, operator 2026-10-15): solid red
 *  and blue with white text. The panel of the loser is grey, so the winner
 *  is easy to see. */
export const PANEL_COLOR: Record<Corner, Record<PanelState, string>> = {
  red: { live: 'bg-red-700 text-white', winner: 'bg-red-600 text-white', loser: 'bg-slate-200 text-slate-400' },
  blue: { live: 'bg-blue-700 text-white', winner: 'bg-blue-600 text-white', loser: 'bg-slate-200 text-slate-400' },
};
/** The small text of a panel (the corner label, the team), the border of its
 *  country pill, and the colour of the Winner label on its white pill. */
export const PANEL_SOFT: Record<Corner, { text: string; border: string; loser: string; winner: string }> = {
  red: { text: 'text-red-100', border: 'border-red-100', loser: 'text-red-400', winner: 'text-red-700' },
  blue: { text: 'text-blue-100', border: 'border-blue-100', loser: 'text-blue-400', winner: 'text-blue-700' },
};

/** The small text of a panel by state: the loser has its own grey (`PANEL_SOFT.loser`), so none here.
 *  The card of the next bout (`'next'`) takes the live look. */
export const softText = (corner: Corner, state: PanelState | 'next'): string => (state === 'loser' ? '' : PANEL_SOFT[corner].text);

/** The border of the country pill by state: grey on a loser, white on a winner, the soft border of the corner else. */
export const countryBorder = (corner: Corner, state: PanelState | 'next'): string =>
  state === 'loser' ? 'border-slate-400' : state === 'winner' ? 'border-white' : PANEL_SOFT[corner].border;

/** The state of each corner on a result: the winner, the loser, or both live on a draw. */
export function panelStateOf(winner: string | null | undefined, corner: Corner): PanelState {
  return !winner || winner === 'draw' ? 'live' : winner === corner ? 'winner' : 'loser';
}

/** The round of a stop: the round that ended a bout before its last round, else null. */
export const stopRound = (bout: Bout, verdict: BoutVerdict): number | null =>
  verdict.round_ended && verdict.round_ended < boutRounds(bout) ? verdict.round_ended : null;

/** The corner that won a round, or null for an even round. */
export const roundWinner = (s: PublicRoundScore): Corner | null =>
  s.red_points > s.blue_points ? 'red' : s.blue_points > s.red_points ? 'blue' : null;

/** What a ring screen shows now. */
export type RingView =
  | { kind: 'live'; bout: Bout; scores: PublicRoundScore[] }
  | { kind: 'result'; bout: Bout; verdict: BoutVerdict }
  | { kind: 'next'; bout: Bout }
  | { kind: 'none' };

/** The view of a ring screen: the result for a short time after a verdict,
 *  the live bout, the next bout, or nothing. A live bout shows at once on
 *  both screens: the timer of the jury is a backup, and no screen waits for
 *  it (operator, 2026-10-15). */
export function ringViewOf(feed: RingFeed): RingView {
  if (feed.flashResult && feed.currentBout) return { kind: 'result', bout: feed.currentBout, verdict: feed.flashResult };
  if (feed.currentBout) return { kind: 'live', bout: feed.currentBout, scores: feed.publicScores };
  return feed.nextBout ? { kind: 'next', bout: feed.nextBout } : { kind: 'none' };
}

/** The scale of the design frame on this screen. The frame keeps 16:9. */
export function useFrameScale(): number {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const fit = () => setScale(Math.min(window.innerWidth / FRAME_W, window.innerHeight / FRAME_H));
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, []);
  return scale;
}
